import { useState } from "react";
import { Button, Container, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, isAfter, isValid } from "date-fns";
import CustomBreadcrumbs from "src/components/custom-breadcrumbs";
import { useSettingsContext } from "src/components/settings";
import { paths } from "src/routes/paths";
import { useRanking } from "../hooks/use-ranking";
import { useConfetti } from "../hooks/use-confetti";
import RankingContent from "./ranking-content";

// ----------------------------------------------------------------------

const today = new Date();

function toDateStr(date: Date | null): string | undefined {
  return date && isValid(date) ? format(date, "yyyy-MM-dd") : undefined;
}

export default function RankingView() {
  const settings = useSettingsContext();

  // Estado de rascunho (o que está nos pickers)
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);

  // Estado aplicado (o que é enviado ao back)
  const [appliedStart, setAppliedStart] = useState<Date | null>(null);
  const [appliedEnd, setAppliedEnd] = useState<Date | null>(null);

  const { rankedSellers, rankedPositions, loading } = useRanking({
    startDate: toDateStr(appliedStart),
    endDate: toDateStr(appliedEnd),
  });

  // Confetes - SOMENTE na URL "/dashboard/ranking"
  useConfetti(500);

  const endDateError =
    draftStart && isValid(draftStart) && draftEnd && isValid(draftEnd) && !isAfter(draftEnd, draftStart)
      ? "A data de fim deve ser maior que a de início"
      : undefined;

  const canApply =
    !endDateError &&
    draftStart !== null &&
    draftEnd !== null &&
    (draftStart !== appliedStart || draftEnd !== appliedEnd);

  const handleApply = () => {
    setAppliedStart(draftStart);
    setAppliedEnd(draftEnd);
  };

  const handleClear = () => {
    setDraftStart(null);
    setDraftEnd(null);
    setAppliedStart(null);
    setAppliedEnd(null);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : "lg"}>
      <CustomBreadcrumbs
        heading="🏆 Ranking de Operadores"
        links={[
          { name: "Mercado" },
          { name: "Operadores", href: paths.dashboard.sellers.root },
          { name: "Ranking" },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "flex-start" }}
        sx={{ mb: 3 }}
      >
        <DatePicker
          label="Data de início"
          value={draftStart}
          onChange={(val) => {
            const next = val && isValid(val) ? val : null;
            setDraftStart(next);
            if (draftEnd && next && !isAfter(draftEnd, next)) {
              setDraftEnd(null);
            }
          }}
          maxDate={today}
          slotProps={{ textField: { size: "small" } }}
        />
        <DatePicker
          label="Data de fim"
          value={draftEnd}
          onChange={(val) => setDraftEnd(val && isValid(val) ? val : null)}
          minDate={draftStart && isValid(draftStart) ? draftStart : undefined}
          maxDate={today}
          slotProps={{
            textField: {
              size: "small",
              error: !!endDateError,
              helperText: endDateError,
            },
          }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="medium"
            onClick={handleApply}
            disabled={!canApply}
          >
            Aplicar
          </Button>
          <Button
            variant="outlined"
            size="medium"
            color="inherit"
            onClick={handleClear}
            disabled={!draftStart && !draftEnd && !appliedStart && !appliedEnd}
          >
            Limpar
          </Button>
        </Stack>
      </Stack>

      <RankingContent
        rankedSellers={rankedSellers}
        rankedPositions={rankedPositions}
        loading={loading}
        maxWidth={settings.themeStretch ? false : "lg"}
      />
    </Container>
  );
}
